import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Briefcase, ChevronDown, Loader2, Plus, Users } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  useShopListings, useCreateListing, useUpdateListing,
  useListingApplicants, useUpdateApplicationStatus,
  type WorkforceListing,
} from '@/hooks/useWorkforceApi';

const ROLE_OPTIONS = [
  { value: 'student', label: 'Student / Intern' },
  { value: 'technician', label: 'Technician' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'manager', label: 'Manager' },
];

const APP_STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

// ---------- Main component ----------

export function WorkforceListingsManager() {
  // FIX-R134-HIGH-1: gate the workforce listings manager to manager-tier
  // (OWNER / MANAGER / SERVICE_WRITER / ORG_ADMIN) only — hiring is a
  // manager-tier responsibility, not a shop-floor activity. The companion
  // RLS migration (20260425170000) prevents non-manager-tier users from
  // INSERT/UPDATE/DELETE on workforce_listings + UPDATE on
  // workforce_applications even if they bypass this UI gate. Non-managers
  // who reach this surface (TECHNICIAN, DISPATCHER) see no card.
  const { activeShop, membership } = useAuth();
  const { data: listings, isLoading } = useShopListings(activeShop?.id);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!activeShop) return null;

  const role = membership?.role ?? '';
  const isManagerTier =
    role === 'OWNER'
    || role === 'MANAGER'
    || role === 'SERVICE_WRITER'
    || role === 'ORG_ADMIN';
  if (!isManagerTier) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Workforce Listings</CardTitle>
          </div>
          <CreateListingDialog
            shopId={activeShop.id}
            open={dialogOpen}
            onOpenChange={setDialogOpen}
          />
        </div>
        <CardDescription>
          Create and manage internship, apprenticeship, and job listings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !listings?.length ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No listings yet. Create one to start receiving applications.
          </p>
        ) : (
          <div className="space-y-3">
            {listings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Create listing dialog ----------

function CreateListingDialog({
  shopId,
  open,
  onOpenChange,
}: {
  shopId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const createMutation = useCreateListing();
  const [title, setTitle] = useState('');
  const [roleTarget, setRoleTarget] = useState('student');
  const [description, setDescription] = useState('');
  const [paid, setPaid] = useState(false);
  const [locationZip, setLocationZip] = useState('');

  const reset = () => {
    setTitle('');
    setRoleTarget('student');
    setDescription('');
    setPaid(false);
    setLocationZip('');
  };

  const handleSubmit = () => {
    createMutation.mutate(
      {
        shop_id: shopId,
        title: title.trim(),
        role_target: roleTarget,
        description: description.trim() || undefined,
        paid,
        location_zip: locationZip.trim() || undefined,
      },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1 text-xs">
          <Plus className="h-3.5 w-3.5" /> New Listing
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Workforce Listing</DialogTitle>
          <DialogDescription>
            Post an opportunity for students, technicians, or other roles.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="wf-title">Title</Label>
            <Input id="wf-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Summer Apprenticeship" />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={roleTarget} onValueChange={setRoleTarget}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wf-desc">Description</Label>
            <Textarea id="wf-desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe the opportunity…" rows={3} />
          </div>
          <div className="flex items-center gap-2">
            <Switch id="wf-paid" checked={paid} onCheckedChange={setPaid} />
            <Label htmlFor="wf-paid">Paid position</Label>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="wf-zip">Location ZIP (optional)</Label>
            <Input id="wf-zip" value={locationZip} onChange={e => setLocationZip(e.target.value)} placeholder="e.g. 90210" maxLength={10} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!title.trim() || createMutation.isPending}>
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Single listing card with applicants ----------

function ListingCard({ listing }: { listing: WorkforceListing }) {
  const [open, setOpen] = useState(false);
  const updateMutation = useUpdateListing();

  const toggleActive = () => {
    updateMutation.mutate({ id: listing.id, is_active: !listing.is_active });
  };

  const roleLabel = ROLE_OPTIONS.find(o => o.value === listing.role_target)?.label ?? listing.role_target;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-lg border p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <div className="min-w-0">
              <h4 className="text-sm font-medium truncate">{listing.title}</h4>
              <p className="text-xs text-muted-foreground">{roleLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {listing.paid && <Badge variant="secondary" className="text-xs">Paid</Badge>}
            <Badge variant={listing.is_active ? 'default' : 'outline'} className="text-xs">
              {listing.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <Switch
              checked={listing.is_active}
              onCheckedChange={toggleActive}
              disabled={updateMutation.isPending}
              aria-label="Toggle active"
            />
          </div>
        </div>

        <CollapsibleContent>
          {listing.description && (
            <p className="text-xs text-muted-foreground pl-8 pb-2">{listing.description}</p>
          )}
          <ApplicantList listingId={listing.id} />
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ---------- Applicant list ----------

function ApplicantList({ listingId }: { listingId: string }) {
  const { data: applicants, isLoading } = useListingApplicants(listingId);
  const statusMutation = useUpdateApplicationStatus();

  if (isLoading) {
    return (
      <div className="pl-8 py-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading applicants…
      </div>
    );
  }

  if (!applicants?.length) {
    return (
      <div className="pl-8 py-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Users className="h-3.5 w-3.5" /> No applicants yet.
      </div>
    );
  }

  return (
    <div className="pl-8 space-y-2 pt-1">
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
        <Users className="h-3.5 w-3.5" /> {applicants.length} applicant{applicants.length !== 1 ? 's' : ''}
      </p>
      {applicants.map((app: any) => {
        const name = app.applicant_name ?? 'Unknown';
        return (
          <div key={app.id} className="flex items-center justify-between rounded border px-3 py-2">
            <div>
              <p className="text-sm font-medium">{name}</p>
              <p className="text-xs text-muted-foreground">
                Applied {new Date(app.created_at).toLocaleDateString()}
              </p>
            </div>
            <Select
              value={app.status}
              onValueChange={(val) => statusMutation.mutate({ id: app.id, status: val })}
              disabled={statusMutation.isPending}
            >
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {APP_STATUS_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      })}
    </div>
  );
}
